import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, SendIcon } from 'lucide-react';
export function EarlyAccessForm() {
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  if (submitted) {
    return <section id="early-access" className="py-24 bg-white" data-id="element-53">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-54">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-blue-50 border border-blue-100 p-12 rounded-3xl" data-id="element-55">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-56">
              <CheckCircle2Icon className="w-10 h-10 text-white" data-id="element-57" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-58">
              Thank you for requesting early access!
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-59">
              We have received your information and will notify you as launch
              updates, service previews, and onboarding opportunities become
              available before the anticipated August 1, 2026 launch.
            </p>
            <button onClick={() => setSubmitted(false)} className="mt-8 text-blue-600 font-semibold hover:underline" data-id="element-60">
              Submit another request
            </button>
          </motion.div>
        </div>
      </section>;
  }
  return <section id="early-access" className="py-24 bg-slate-50" data-id="element-61">
      <div className="max-w-4xl mx-auto px-4" data-id="element-62">
        <div className="text-center mb-12" data-id="element-63">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-64">
            Request Early Access
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-65">
            We are currently preparing BizCloudDesk for early business users,
            with an anticipated launch date of August 1, 2026. Complete the form
            below to request early access, receive launch notifications, and let
            us know which services your business may need first.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100" data-id="element-66">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-67">
            <div className="space-y-2" data-id="element-68">
              <label className="text-sm font-semibold text-slate-700" data-id="element-69">
                Full Name
              </label>
              <input required type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="John Doe" data-id="element-70" />
            </div>
            <div className="space-y-2" data-id="element-71">
              <label className="text-sm font-semibold text-slate-700" data-id="element-72">
                Business Name
              </label>
              <input required type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="Acme Corp" data-id="element-73" />
            </div>
            <div className="space-y-2" data-id="element-74">
              <label className="text-sm font-semibold text-slate-700" data-id="element-75">
                Email Address
              </label>
              <input required type="email" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="john@example.com" data-id="element-76" />
            </div>
            <div className="space-y-2" data-id="element-77">
              <label className="text-sm font-semibold text-slate-700" data-id="element-78">
                Phone Number
              </label>
              <input required type="tel" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="(555) 000-0000" data-id="element-79" />
            </div>
            <div className="space-y-2" data-id="element-80">
              <label className="text-sm font-semibold text-slate-700" data-id="element-81">
                Website (if applicable)
              </label>
              <input type="url" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="https://..." data-id="element-82" />
            </div>
            <div className="space-y-2" data-id="element-83">
              <label className="text-sm font-semibold text-slate-700" data-id="element-84">
                Business Type / Industry
              </label>
              <input required type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="e.g. Consulting, Retail" data-id="element-85" />
            </div>
          </div>

          <div className="mb-8" data-id="element-86">
            <label className="text-sm font-semibold text-slate-700 block mb-4" data-id="element-87">
              Current Business Stage
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-88">
              {['Starting a new business', 'Existing business', 'Growing or expanding', 'Need better systems', 'Need help moving from another provider'].map(stage => <label key={stage} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-89">
                  <input type="radio" name="stage" className="w-4 h-4 text-blue-600" data-id="element-90" />
                  <span className="text-sm text-slate-600" data-id="element-91">{stage}</span>
                </label>)}
            </div>
          </div>

          <div className="mb-8" data-id="element-92">
            <label className="text-sm font-semibold text-slate-700 block mb-4" data-id="element-93">
              Services of Interest
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-94">
              {['Website hosting', 'Website design or development', 'CRM setup', 'VoIP / business phone service', 'Client intake forms', 'Marketing automation', 'Online booking', 'Customer support tools', 'Cloud storage or file management', 'Social media integration', 'E-commerce or payment tools', 'Not sure yet, I need guidance'].map(service => <label key={service} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-95">
                  <input type="checkbox" className="w-4 h-4 rounded text-blue-600" data-id="element-96" />
                  <span className="text-xs text-slate-600" data-id="element-97">{service}</span>
                </label>)}
            </div>
          </div>

          <div className="mb-8" data-id="element-98">
            <label className="text-sm font-semibold text-slate-700 block mb-4" data-id="element-99">
              Preferred Launch Notification Method
            </label>
            <div className="flex flex-wrap gap-4" data-id="element-100">
              {['Email', 'Text message', 'Phone call', 'No preference'].map(method => <label key={method} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-101">
                    <input type="radio" name="notification" className="w-4 h-4 text-blue-600" data-id="element-102" />
                    <span className="text-sm text-slate-600" data-id="element-103">{method}</span>
                  </label>)}
            </div>
          </div>

          <div className="space-y-2 mb-8" data-id="element-104">
            <label className="text-sm font-semibold text-slate-700" data-id="element-105">
              Message / Business Needs
            </label>
            <textarea className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all h-32" placeholder="Tell us more about what your business needs..." data-id="element-106"></textarea>
          </div>

          <div className="flex items-start gap-3 mb-8" data-id="element-107">
            <input required type="checkbox" className="mt-1 w-4 h-4 rounded text-blue-600" data-id="element-108" />
            <span className="text-sm text-slate-600" data-id="element-109">
              I agree to receive updates regarding BizCloudDesk, early access
              opportunities, and launch notifications.
            </span>
          </div>

          <button type="submit" className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2" data-id="element-110">
            Join the Early Access List
            <SendIcon className="w-5 h-5" data-id="element-111" />
          </button>
        </form>
      </div>
    </section>;
}